import { useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import axios from "axios";
import { FilmSlate, ArrowLeft } from "@phosphor-icons/react";
import { Header } from "@/components/Header";
import { VideoCard } from "@/components/VideoCard";
import { Helmet } from "react-helmet-async";
import { ShareRow } from "@/components/ShareRow";
import { applyOg } from "@/lib/og";
import { API } from "@/lib/api";

const ROLE = {
  director: { api: "directors", eyebrow: "Filmmaker", verb: "directed by" },
  actor: { api: "actors", eyebrow: "Actor", verb: "starring" },
};

const PersonShare = ({ kind, name, count, verb }) => {
  const origin = window.location.origin;
  const slug = encodeURIComponent(name);
  useEffect(() => applyOg({
    title: `${name} Movies — Watch Free on ReelVault`,
    description: `${count} free film${count === 1 ? "" : "s"} ${verb} ${name} on ReelVault.`,
    image: `${origin}/api/og/person/${kind}/${slug}/image.jpg`,
    url: `${origin}/${kind}/${slug}`,
    type: "website",
  }), [kind, name, count, verb, origin, slug]);
  return (
    <div data-testid={`${kind}-share`}>
      <ShareRow title={name} url={`${origin}/api/og/person/${kind}/${slug}`} text={`Watch ${name} movies free on ReelVault!`} />
    </div>
  );
};

export default function Person({ kind = "director" }) {
  const { name } = useParams();
  const role = ROLE[kind];
  const { data, isLoading, isError } = useQuery({
    queryKey: [kind, name],
    queryFn: async () => (await axios.get(`${API}/${role.api}/${encodeURIComponent(name)}`)).data,
  });
  const display = data?.name || name;
  const years = (data?.items || []).map((v) => v.release_year).filter(Boolean);
  return (
    <div className="min-h-screen bg-[#050505]">
      <Helmet>
        <title>{`${display} — Films on ReelVault`}</title>
        <meta name="description" content={`Watch films ${role.verb} ${display} free on ReelVault.`} />
      </Helmet>
      <Header />
      <main data-testid={`${kind}-page`} className="mx-auto max-w-[1600px] min-[2200px]:max-w-[2200px] px-5 sm:px-8 py-12 rv-fade">
        <Link to="/" data-testid={`${kind}-back`} className="mb-8 inline-flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.2em] text-white/40 transition-colors duration-200 hover:text-amber">
          <ArrowLeft size={14} /> Browse
        </Link>
        <p className="font-mono text-[11px] uppercase tracking-[0.3em] text-amber">{role.eyebrow}</p>
        <h1 data-testid={`${kind}-name`} className="mt-2 font-serif text-4xl sm:text-5xl lg:text-6xl tracking-tighter text-[#EDEDED]">{display}</h1>
        {data && (
          <p data-testid={`${kind}-count`} className="mt-3 font-mono text-xs uppercase tracking-[0.2em] text-white/40">
            {data.count} film{data.count === 1 ? "" : "s"} in the vault{years.length > 1 ? ` · ${Math.min(...years)}–${Math.max(...years)}` : ""}
          </p>
        )}
        {data && (
          <PersonShare kind={kind} name={display} count={data.count} verb={role.verb} />
        )}
        <div className="mt-10">
          {isLoading && (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 min-[1600px]:grid-cols-5 gap-6 lg:gap-8">
              {Array.from({ length: 8 }).map((_, i) => <div key={i} className="aspect-video animate-pulse border border-white/5 bg-ink-surface" />)}
            </div>
          )}
          {isError && (
            <div data-testid={`${kind}-empty`} className="py-20">
              <FilmSlate size={48} weight="light" className="mb-4 text-white/20" />
              <p className="font-serif text-2xl text-white/70">No films {role.verb} {display} in the vault yet.</p>
            </div>
          )}
          {data && (
            <div data-testid={`${kind}-grid`} className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 min-[1600px]:grid-cols-5 min-[2200px]:grid-cols-6 gap-6 lg:gap-8">
              {data.items.map((v, i) => <VideoCard key={v.video_id} video={v} index={i} />)}
            </div>
          )}
        </div>
      </main>
    </div>
  );
}
